<template>
  <Teleport defer to="ion-app">
    <transition name="pib-fade">
      <div
        v-if="modelValue"
        v-bind="privateRegionAttrs"
        class="pib-overlay"
        :class="themeClass"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pib-title"
        @click="$emit('close')"
      >
        <div class="pib-card" @click.stop>
          <div class="pib-text">
            <h2 id="pib-title" class="pib-title">Interested to sign up?</h2>
            <p class="pib-subtitle">Ask a HOGC leader to share more with you!</p>
          </div>

          <button type="button" class="pib-btn pib-btn--primary" @click="$emit('close')">
            Got it
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import coursesTheme from '@/mixins/CoursesTheme';
import privateRegion from '@/mixins/PrivateRegion';

// Shown when a member who can SEE a course but is not eligible to buy it (an N
// or V — the server's purchaseBlockReason is 'ROLE') taps a locked lesson's
// "Sign up". Rather than open a payment sheet that would be refused, it invites
// them to ask a HOGC leader.
//
// A plain v-if overlay + fade, like its dismissable siblings CourseCompleteModal
// and UnfilledBlanksModal — NOT ion-modal. It only informs, so it needs none of
// the programmatic present/dismiss machinery CgHardBlockModal uses to ENFORCE
// can-dismiss=false. The parent owns the open flag (one-way :model-value); the
// backdrop and the button both emit `close`, and the parent lowers the flag.
export default {
  name: 'PurchaseIneligibleModal',
  // themeClass makes the --courses-* tokens resolve inside this Teleported modal,
  // so it follows the brown Next Steps palette (and dark mode) rather than falling
  // back to defaults under <ion-app>.
  // privateRegion: teleported out of the page root, so the page's stamp does
  // not reach it — the attributes are bound on the overlay above.
  mixins: [coursesTheme, privateRegion],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
};
</script>

<style scoped>
.pib-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  padding-top: max(16px, env(safe-area-inset-top, 16px));
  padding-bottom: max(16px, env(safe-area-inset-bottom, 16px));
  background-color: rgba(30, 27, 23, 0.5);
}

.pib-card {
  display: flex;
  width: 100%;
  max-width: 300px;
  flex-direction: column;
  gap: 20px;
  padding: 28px;
  text-align: center;
  background-color: var(--courses-surface-raised);
  border-radius: 20px;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
}

.pib-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pib-title {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.25;
  color: var(--courses-ink-strong);
}

.pib-subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.65;
  color: var(--courses-ink-muted);
}

.pib-btn {
  width: 100%;
  border-radius: 100px;
  cursor: pointer;
}

.pib-btn--primary {
  padding: 14px 0;
  font-size: 14px;
  font-weight: 700;
  /* Inverted pair: both tokens flip in dark mode, so the label stays legible on
     the fill in either theme. */
  color: var(--courses-surface-raised);
  background-color: var(--courses-ink-strong);
  border: none;
}

/* sm breakpoint (640px) */
@media (min-width: 640px) {
  .pib-card {
    max-width: 380px;
    gap: 24px;
    padding: 36px;
  }
  .pib-title {
    font-size: 24px;
  }
  .pib-subtitle {
    font-size: 16px;
  }
  .pib-btn--primary {
    padding: 16px 0;
    font-size: 16px;
  }
}

/* md breakpoint (768px) */
@media (min-width: 768px) {
  .pib-card {
    max-width: 440px;
    padding: 40px;
  }
  .pib-title {
    font-size: 30px;
  }
  .pib-subtitle {
    font-size: 18px;
  }
  .pib-btn--primary {
    padding: 20px 0;
    font-size: 18px;
  }
}

/* Fade transition */
.pib-fade-enter-active,
.pib-fade-leave-active {
  transition: opacity 0.2s ease;
}
.pib-fade-enter-from,
.pib-fade-leave-to {
  opacity: 0;
}
</style>
